/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { noop } from 'src/core/utils/base';
import { OverallChart } from 'src/luis/projects/analytics/components/OverallPredictionsCard/OverallChart/OverallChart';
import { ProblemsSection } from 'src/luis/projects/analytics/components/OverallPredictionsCard/ProblemsSection/ProblemsSection';
import { PassedFilters } from 'src/luis/projects/analytics/types/PassedFilters';
import { LuisId } from 'src/luis/types/Resource';

export type OverallPredictionsCardProps = {
    /**
     * A callback for when an intent is clicked.
     */
    onIntentClick: (intentId: LuisId, filters: PassedFilters) => void;
};

const dashboardTheme = getScopedTheme('Dashboard');

/**
 * Represents the overall predictions card.
 */
export const OverallPredictionsCard = observer((props: OverallPredictionsCardProps) => {
    const { onIntentClick = noop } = props;
    const { isMediumOrGreater } = useResponsiveMode();
    const { dispatcher, rootStore } = useGlobalState();

    const currentAppStore = rootStore.currentLuisAppStore;
    const { analyticsStore } = currentAppStore.currentAppVersionStore;

    const upToDateVersionIds = currentAppStore.appVersionStoreHouse.items
        .filter(v => v.id !== currentAppStore.activeVersion && v.trainedDate !== null)
        .map(v => v.id);

    const content = (
        <>
            <StackItem align="stretch" grow={1}>
                <OverallChart
                    comparableVersionIds={upToDateVersionIds}
                    onVersionComparisonChange={(selectedComparedVersionId: string) => {
                        if (selectedComparedVersionId !== 'none') {
                            dispatcher.dispatch('compareWithVersion', { id: selectedComparedVersionId });
                        }
                    }}
                />
            </StackItem>
            <StackItem>
                <ProblemsSection
                    onIntentClick={onIntentClick}
                    needyIntents={analyticsStore.needyIntents}
                    greedyIntents={analyticsStore.greedyIntents}
                    unclearIntents={analyticsStore.unclearIntents}
                    incorrectIntents={analyticsStore.incorrectIntents}
                />
            </StackItem>
        </>
    );

    return !isMediumOrGreater ? (
        <Stack tokens={{ childrenGap: 20, padding: dashboardTheme.detailedFaultyIntentPercentLegendSpacing }}>{content}</Stack>
    ) : (
        <Stack
            verticalAlign="center"
            horizontal
            tokens={{ childrenGap: 10, padding: dashboardTheme.detailedFaultyIntentPercentLegendSpacing }}>
            {content}
        </Stack>
    );
});
